/**
 * Turning one kind of thing on the picture into another (a shape into a spot, a spot into a
 * shape or a way in/out, a way in/out into a spot).
 *
 * Pure document maths: {@link convertInDoc} edits a (draft) stage document in place and says what
 * it did; the editor store wraps it in ONE commit (`convertFeatures`), so every conversion is one
 * undo step, nothing is saved until the person presses Save, and the live lint re-checks it.
 *
 * Rules the backend schema sets (backend/app/annotation/models.py):
 *  - ids are unique across the whole document, so a feature that moves to another list keeps
 *    its id (its visibility, its place in the selection and any link to it stay valid);
 *  - …and that id is also tombstoned. The merge (`merge.py`) matches a suggestion by id **within
 *    one list**, and the plate heuristics always propose the same ids (`r_ground`, `p_left`), so
 *    without it the next re-run would put the old shape / way in/out back beside the new spot
 *    under the SAME id — two features with one id. The moved feature is a human edit (locked), so
 *    a re-run could not change it anyway; the tombstone only stops the old one coming back;
 *  - a way in/out carries no `at` / `label` — inside the picture it stands on a spot
 *    (`anchor_id`, the same pair `verify.repair_legacy_portals` makes), else on an `edge`;
 *  - `anchor.region_id` and a walk-behind layer's `source.region_id` name a shape; a way in/out's
 *    `anchor_id` names a spot. No conversion leaves one of those pointing at nothing.
 */
import { clampPct, clampPt, normalizeWinding, rectRing, round2 } from '@/geo/polygon'
import type { Poly, Pt, Ring } from '@/geo/types'
import type { PortalEdge, Provenance, StageBody } from '@/schema'

export type ConvertDomain = 'region_kind' | 'anchor_kind' | 'portal_kind' | 'path_kind'

/** Which OTHER lists a feature of each list can become (paths only change kind). */
export const CONVERSIONS: Readonly<Record<ConvertDomain, readonly ConvertDomain[]>> = {
  region_kind: ['anchor_kind'],
  anchor_kind: ['region_kind', 'portal_kind'],
  portal_kind: ['anchor_kind'],
  path_kind: [],
}

export type SkipReason = 'no_conversion' | 'layer_source' | 'missing'

export interface ConvertOutcome {
  /** What to select afterwards: every result, in the order asked. */
  selected: string[]
  /** Features that moved to another list (or a way in/out added on a spot). */
  converted: string[]
  /** Features that were already of the target list and only changed kind. */
  retagged: string[]
  /** Ids that no longer exist (tombstoned: a re-run may not bring them back). */
  removed: string[]
  /** Features that could not be changed, and why. */
  skipped: { id: string; reason: SkipReason }[]
  /** Ways in/out whose spot became a shape: they now use this edge of the picture. */
  rehomed: { id: string; edge: PortalEdge }[]
}

export interface ConvertContext {
  /** Provenance stamped on everything touched (a human edit: locked, confidence 1). */
  provenance: () => Provenance
  /** A fresh document-unique id with this prefix (`p` for a new way in/out). */
  newId: (prefix: string) => string
}

/** The list a feature id is in, or undefined (layers and unknown ids). */
export function listOf(doc: StageBody, id: string): ConvertDomain | undefined {
  if (doc.regions.some((f) => f.id === id)) return 'region_kind'
  if (doc.anchors.some((f) => f.id === id)) return 'anchor_kind'
  if (doc.portals.some((f) => f.id === id)) return 'portal_kind'
  if (doc.paths.some((f) => f.id === id)) return 'path_kind'
  return undefined
}

/** Can a feature of `from` become one of `to` (the same list = a change of kind)? */
export function canConvert(from: ConvertDomain, to: ConvertDomain): boolean {
  return from === to || CONVERSIONS[from].includes(to)
}

/** The other lists some of `ids` could become, in a fixed order (shape, spot, way in/out). */
export function conversionTargets(doc: StageBody | null, ids: readonly string[]): ConvertDomain[] {
  if (!doc) return []
  const from = new Set(ids.map((id) => listOf(doc, id)).filter((d): d is ConvertDomain => d !== undefined))
  const order: ConvertDomain[] = ['region_kind', 'anchor_kind', 'portal_kind', 'path_kind']
  return order.filter((to) => [...from].some((d) => d !== to && CONVERSIONS[d].includes(to)))
}

/** Why `id` cannot become `to` (undefined: it can). */
export function blockedReason(doc: StageBody, id: string, to: ConvertDomain): SkipReason | undefined {
  const from = listOf(doc, id)
  if (!from) return 'missing'
  if (!canConvert(from, to)) return 'no_conversion'
  if (from === 'region_kind' && to !== 'region_kind' && doc.layers.some((l) => 'region_id' in l.source && l.source.region_id === id)) return 'layer_source'
  return undefined
}

/* -------------------------------------------------------------------------- */
/* Geometry                                                                    */
/* -------------------------------------------------------------------------- */

/**
 * Where a shape touches the floor: under the middle of its box, the lowest point of its outline
 * (for a box, its bottom-centre). Pictures run y downwards, so "lowest" is the largest y.
 */
export function floorContact(ring: Ring): Pt {
  if (ring.length === 0) return [50, 50]
  const xs = ring.map((p) => p[0])
  const ys = ring.map((p) => p[1])
  const cx = (Math.min(...xs) + Math.max(...xs)) / 2
  let best = Number.NEGATIVE_INFINITY
  for (let i = 0; i < ring.length; i++) {
    const a = ring[i]!
    const b = ring[(i + 1) % ring.length]!
    const lo = Math.min(a[0], b[0])
    const hi = Math.max(a[0], b[0])
    if (cx < lo || cx > hi) continue
    const y = a[0] === b[0] ? Math.max(a[1], b[1]) : a[1] + ((cx - a[0]) / (b[0] - a[0])) * (b[1] - a[1])
    if (y > best) best = y
  }
  const y = Number.isFinite(best) ? best : Math.max(...ys)
  return [round2(clampPct(cx)), round2(clampPct(y))]
}

/** Width and height (percent) of the shape a spot without an outline becomes. */
export const SPOT_SHAPE_SIZE = 6

/**
 * The outline a spot becomes. Its own footprint when it has one (moved to where the spot is now),
 * else a small box standing on the spot — so turning it back gives the same spot.
 */
export function spotShape(at: Pt, footprint?: Poly): Poly {
  if (footprint && footprint.outer.length >= 3) {
    const [cx, cy] = floorContact(footprint.outer)
    const d: Pt = [at[0] - cx, at[1] - cy]
    const move = (r: Ring): Ring => r.map((p) => clampPt([round2(p[0] + d[0]), round2(p[1] + d[1])]))
    return normalizeWinding({ outer: move(footprint.outer), ...(footprint.holes?.length ? { holes: footprint.holes.map(move) } : {}) })
  }
  const s = SPOT_SHAPE_SIZE
  let left = at[0] - s / 2
  let right = at[0] + s / 2
  if (left < 0) [left, right] = [0, s]
  if (right > 100) [left, right] = [100 - s, 100]
  let top = at[1] - s
  let bottom = at[1]
  if (top < 0) [top, bottom] = [0, Math.max(bottom, s)]
  return normalizeWinding({ outer: rectRing([round2(left), round2(top)], [round2(right), round2(bottom)]) })
}

/** The picture edge closest to a point. */
export function nearestEdge(at: Pt): PortalEdge {
  const d: [PortalEdge, number][] = [
    ['left', at[0]],
    ['right', 100 - at[0]],
    ['top', at[1]],
    ['bottom', 100 - at[1]],
  ]
  return d.reduce((a, b) => (b[1] < a[1] ? b : a))[0]
}

/** Where a spot for an edge way in/out goes: just inside that edge, on the ground for the sides. */
export function edgeSpot(edge: PortalEdge | undefined, depth: StageBody['depth']): Pt {
  const horizon = depth.mode === 'horizon' && typeof depth.horizon_y === 'number' ? depth.horizon_y : undefined
  const groundY = round2(horizon !== undefined ? Math.min(96, horizon + (100 - horizon) * 0.6) : 85)
  switch (edge) {
    case 'left':
      return [2, groundY]
    case 'right':
      return [98, groundY]
    case 'top':
      return [50, 4]
    case 'bottom':
      return [50, 96]
    default:
      return [50, groundY]
  }
}

/* -------------------------------------------------------------------------- */
/* The edit                                                                    */
/* -------------------------------------------------------------------------- */

const EMPTY = (): ConvertOutcome => ({ selected: [], converted: [], retagged: [], removed: [], skipped: [], rehomed: [] })

type Plain<T> = { -readonly [K in keyof T]: T[K] }

/** No suggestion may bring `id` back into the list it left (see the file comment). */
function retire(d: StageBody, id: string): void {
  if (!d.tombstones.includes(id)) d.tombstones.push(id)
}

/** Did the conversion change anything, or was everything already what it was asked to become? */
export function changedAnything(out: ConvertOutcome): boolean {
  return out.converted.length + out.retagged.length + out.removed.length + out.rehomed.length > 0
}

/**
 * Make every feature of `ids` a `to` of kind `kind`, in place. Features already in `to` only
 * change kind; the rest follow {@link CONVERSIONS}; anything else is left alone and reported in
 * `skipped`. Links stay valid (see the file comment). Changes nothing when nothing applies.
 */
export function convertInDoc(d: StageBody, ids: readonly string[], to: ConvertDomain, kind: string, ctx: ConvertContext): ConvertOutcome {
  const out = EMPTY()
  const pick = (id: string) => {
    if (!out.selected.includes(id)) out.selected.push(id)
  }

  for (const id of [...new Set(ids)]) {
    const reason = blockedReason(d, id, to)
    if (reason) {
      out.skipped.push({ id, reason })
      continue
    }
    const from = listOf(d, id)!

    /* Same list: only the kind changes. */
    if (from === to) {
      const f = [...d.regions, ...d.anchors, ...d.portals, ...d.paths].find((x) => x.id === id)!
      if (f.kind !== kind) {
        f.kind = kind
        f.provenance = ctx.provenance()
        out.retagged.push(id)
      }
      pick(id)
      continue
    }

    /* Shape → spot: on its floor-contact point, keeping its outline as the spot's footprint. */
    if (from === 'region_kind' && to === 'anchor_kind') {
      const i = d.regions.findIndex((r) => r.id === id)
      const r = d.regions[i]!
      const outline: Poly = { outer: r.poly.outer.map((p) => [p[0], p[1]] as Pt), ...(r.poly.holes?.length ? { holes: r.poly.holes.map((h) => h.map((p) => [p[0], p[1]] as Pt)) } : {}) }
      const spot: Plain<StageBody['anchors'][number]> = {
        id,
        kind,
        ...(r.label ? { label: r.label } : {}),
        at: floorContact(r.poly.outer),
        footprint: outline,
        ...(r.tags?.length ? { tags: [...r.tags] } : {}),
        provenance: ctx.provenance(),
      }
      d.regions.splice(i, 1)
      d.anchors.push(spot)
      retire(d, id)
      // A spot linked to this shape (a tree's walk-behind outline) now links to nothing.
      for (const a of d.anchors) {
        if (a.region_id === id) {
          delete a.region_id
          a.provenance = ctx.provenance()
        }
      }
      out.converted.push(id)
      pick(id)
      continue
    }

    /* Spot → shape: its footprint, or a small box standing on it. */
    if (from === 'anchor_kind' && to === 'region_kind') {
      const i = d.anchors.findIndex((a) => a.id === id)
      const a = d.anchors[i]!
      const shape: Plain<StageBody['regions'][number]> = {
        id,
        kind,
        ...(a.label ? { label: a.label } : {}),
        poly: spotShape(a.at, a.footprint),
        ...(a.tags?.length ? { tags: [...a.tags] } : {}),
        provenance: ctx.provenance(),
      }
      d.anchors.splice(i, 1)
      d.regions.push(shape)
      retire(d, id)
      // A way in/out that stood on it needs somewhere to be: the nearest edge of the picture.
      for (const p of d.portals) {
        if (p.anchor_id !== id) continue
        delete p.anchor_id
        if (!p.edge) p.edge = nearestEdge(a.at)
        p.provenance = ctx.provenance()
        out.rehomed.push({ id: p.id, edge: p.edge })
      }
      out.converted.push(id)
      pick(id)
      continue
    }

    /* Spot → way in/out: a way in/out standing on the spot (the spot stays, as its place). */
    if (from === 'anchor_kind' && to === 'portal_kind') {
      const existing = d.portals.find((p) => p.anchor_id === id)
      if (existing) {
        if (existing.kind !== kind) {
          existing.kind = kind
          existing.provenance = ctx.provenance()
          out.retagged.push(existing.id)
        }
        pick(existing.id)
        continue
      }
      const pid = ctx.newId('p')
      d.portals.push({ id: pid, kind, anchor_id: id, provenance: ctx.provenance() })
      out.converted.push(pid)
      pick(pid)
      continue
    }

    /* Way in/out → spot: its own spot when it stands on one, else a new spot by its edge. */
    if (from === 'portal_kind' && to === 'anchor_kind') {
      const i = d.portals.findIndex((p) => p.id === id)
      const p = d.portals[i]!
      const spot = p.anchor_id ? d.anchors.find((a) => a.id === p.anchor_id) : undefined
      d.portals.splice(i, 1)
      if (spot) {
        if (spot.kind !== kind) {
          spot.kind = kind
          spot.provenance = ctx.provenance()
        }
        // The way in/out is gone for good: a re-run may not put it back.
        retire(d, id)
        out.removed.push(id)
        out.converted.push(spot.id)
        pick(spot.id)
        continue
      }
      d.anchors.push({
        id,
        kind,
        at: edgeSpot(p.edge, d.depth),
        ...(p.tags?.length ? { tags: [...p.tags] } : {}),
        provenance: ctx.provenance(),
      })
      retire(d, id)
      out.converted.push(id)
      pick(id)
      continue
    }

    out.skipped.push({ id, reason: 'no_conversion' })
  }
  return out
}
